import "./App.css";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { setUser, setLogout } from "./redux/features/authSlice";
import { me } from "./services/authService";
import ProductAdd from "./component/Product/ProductAdd";
import ProductEdit from "./component/Product/ProductEdit";
import ProductList from "./component/Product/ProductList";
import PrivateRoute from "./routes/PrivateRoute";
import RequireRole, { STAFF, CUSTOMER, CUSTOMER_ADMIN } from "./routes/RequireRole";
import { Routes, Route } from "react-router-dom";
import Login from "./component/Login/Login";
import VendorList from "./component/Vendor/VendorList";
import VendorAddPrices from "./component/Vendor/VendorPrices";
import VendorUploadPrices from "./component/Vendor/VendorUploadPrices";
import ProductPriceList from "./component/Vendor/ProductPriceList";
import CustomerList from "./component/Customer/CustomerList";
import CustomerAddRequirement from "./component/Customer/CustomerRequirements";
import CustomerRequirementList from "./component/Customer/CustomerRequirementsList";
import ContactList from "./component/Contact/ContactList";
import VendorInbox from "./component/VendorInbox/VendorInbox";
import OrganizationList from "./component/Organization/OrganizationList";
import OrganizationDetail from "./component/Organization/OrganizationDetail";
import PortalLayout from "./component/Portal/PortalLayout";
import PortalHome from "./component/Portal/PortalHome";
import { PortalMore, PortalSites, PortalUsers, PortalNotifications, PortalComingSoon } from "./component/Portal/PortalMore";
import { NewRequirement, RequirementList, RequirementDetail } from "./component/Portal/PortalRequirements";
import Plants from "./component/Portal/Plants";
import RequirementListPage from "./component/Portal/RequirementListPage";
import AccountPage from "./component/Portal/AccountPage";
import Receivables from "./component/Organization/Receivables";
import SocietyBook from "./component/Organization/SocietyBook";
import Projections from "./component/Reports/Projections";
import SocietyAccount from "./component/Organization/SocietyAccount";
import { QuotationList, QuotationDetail } from "./component/Portal/PortalQuotations";
import { OrderList, OrderDetail } from "./component/Portal/PortalOrders";
import RequirementsQueue from "./component/Requirements/RequirementsQueue";
import RequirementDesk from "./component/Requirements/RequirementDesk";
import OrdersQueue from "./component/Orders/OrdersQueue";
import OrderDesk from "./component/Orders/OrderDesk";
import Landing from "./component/Public/Landing";
import RequestAccess from "./component/Public/RequestAccess";
import { isPortalHost } from "./utils/host";
import CatalogueProductList from "./component/Catalogue/ProductList";
import PriceList from "./component/Catalogue/PriceList";
import Dashboard from "./component/Dashboard/Dashboard";
import RateFreshness from "./component/Catalogue/RateFreshness";
import AppUsers from "./component/Catalogue/AppUsers";
import FieldRates from "./component/Catalogue/FieldRates";
import ProductPage from "./component/Catalogue/ProductPage";
import { CatalogueVendorList, CatalogueVendorPage } from "./component/Catalogue/VendorList";

// Two route trees on one app: the CMS for ManBhawan staff (the original
// screens, under PrivateRoute's sidebar layout) and the HortiHub portal for
// customers under /portal. RequireRole sends each signed-in user to the
// tree their role allows.

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((s) => s.auth);
  // Every token is checked with the server before the app trusts it, even
  // when a user object is cached beside it.
  //
  // Trusting the cache meant an expired token still rendered the whole
  // signed-in shell: isAuthenticated only asks whether a token string
  // exists, not whether it still works. The result was a dashboard with no
  // data on it, because every call behind it was being refused.
  const [ready, setReady] = useState(!isAuthenticated);
  useEffect(() => {
    if (ready) return;
    me()
      .then((u) => dispatch(setUser(u)))
      .catch((e) => {
        // Only a refusal ends the session. A server that is down, or a
        // laptop that is briefly offline, must not sign anybody out - the
        // check exists to catch a token the server rejects, not to make the
        // app unusable whenever it cannot reach it.
        if (e && (e.status === 401 || e.status === 403)) {
          localStorage.clear();
          dispatch(setLogout());
        }
      })
      .finally(() => setReady(true));
  }, [ready, dispatch]);
  if (!ready) return null;

  return (
    <>
      <Routes>
        {/* hortihub.in: the public landing page is the root; the CMS host keeps the login there */}
        <Route path="/" element={isPortalHost() ? <Landing /> : <Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/request-access" element={<RequestAccess />} />

        <Route element={<RequireRole roles={STAFF} />}>
          <Route element={<PrivateRoute />}>
            <Route path="/product" element={<ProductList />} />
            <Route path="/save-product" element={<ProductAdd />} />
            <Route path="/vendor" element={<VendorList />} />
            <Route path="/product/:id" element={<ProductEdit />} />
            <Route path="/vendor/prices/:vendorId" element={<VendorAddPrices />} />
            <Route path="/vendor/upload-prices/:vendorId" element={<VendorUploadPrices />} />
            <Route path="/vendor/prices" element={<ProductPriceList />} />
            <Route path="/customer" element={<CustomerList />} />
            <Route path="/customer/:customerId/requirement/:customerRequirementId" element={<CustomerAddRequirement />} />
            <Route path="/customer/:customerId/requirement" element={<CustomerAddRequirement />} />
            <Route path="/customerRequirements" element={<CustomerRequirementList />} />
            <Route path="/contact" element={<ContactList />} />
            <Route path="/vendor-inbox" element={<VendorInbox />} />
            <Route path="/reports" element={<Projections />} />
            <Route path="/societies" element={<SocietyBook />} />
            <Route path="/societies/:accountId" element={<SocietyAccount />} />
            <Route path="/organizations" element={<OrganizationList />} />
            <Route path="/organizations/:id" element={<OrganizationDetail />} />
            <Route path="/requirements" element={<RequirementsQueue />} />
            <Route path="/requirements/:number" element={<RequirementDesk />} />
            <Route path="/orders" element={<OrdersQueue />} />
            <Route path="/orders/:id" element={<OrderDesk />} />
            <Route path="/catalogue/products" element={<CatalogueProductList />} />
            <Route path="/catalogue/price-list" element={<PriceList />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/receivables" element={<Receivables />} />
            <Route path="/catalogue/rate-freshness" element={<RateFreshness />} />
            <Route path="/catalogue/app-users" element={<AppUsers />} />
            <Route path="/catalogue/field-rates" element={<FieldRates />} />
            <Route path="/catalogue/products/:id" element={<ProductPage />} />
            <Route path="/catalogue/vendors" element={<CatalogueVendorList />} />
            <Route path="/catalogue/vendors/:id" element={<CatalogueVendorPage />} />
          </Route>
        </Route>

        <Route element={<RequireRole roles={CUSTOMER} />}>
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalHome />} />
            <Route path="requirements" element={<RequirementList />} />
            <Route path="plants" element={<Plants />} />
            <Route path="list" element={<RequirementListPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="requirements/new" element={<NewRequirement />} />
            <Route path="requirements/:number" element={<RequirementDetail />} />
            <Route path="quotations" element={<QuotationList />} />
            <Route path="quotations/:id" element={<QuotationDetail />} />
            <Route path="orders" element={<OrderList />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="documents" element={<PortalComingSoon title="Documents" />} />
            <Route path="sites" element={<PortalSites />} />
            <Route path="notifications" element={<PortalNotifications />} />
            <Route path="more" element={<PortalMore />} />
            <Route element={<RequireRole roles={CUSTOMER_ADMIN} />}>
              <Route path="users" element={<PortalUsers />} />
            </Route>
          </Route>
        </Route>
      </Routes>
      <ToastContainer />
    </>
  );
}

export default App;
